import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import type { GeoUnit } from '@/features/geo/types/geo.types';
import type { LeaveType } from '@/features/leaveTypes/types/leaveType.types';
import { DISTRICT_FIRST_FIELD_STATE, getDateFromToFilterFields } from '@/utils/filterFields';
import {
  getSelectFieldsWhenOptionsExist,
  toFilterOptions,
  type OptionWithCount,
} from '@/utils/filters';
import {
  APPROVAL_STATUS,
  APPROVAL_STATUS_LABELS,
  DATE_FROM_FILTER_KEY,
  DATE_TO_FILTER_KEY,
  LEAVE_REQUEST_TYPE,
  LEAVE_REQUEST_TYPE_LABELS,
} from '../constants/leaveApplication.constants';
import type { HierarchyFilterOption } from '../types/leaveApplication.types';

const STATUS_OPTIONS: FilterOption[] = APPROVAL_STATUS.map((status) => ({
  label: APPROVAL_STATUS_LABELS[status],
  value: status,
}));

const REQUEST_TYPE_OPTIONS: FilterOption[] = LEAVE_REQUEST_TYPE.map((requestType) => ({
  label: LEAVE_REQUEST_TYPE_LABELS[requestType],
  value: requestType,
}));

interface GetLeaveApplicationFilterFieldsArgs {
  isAttendanceClaim: boolean;
  hasDistrict: boolean;
  divisions: GeoUnit[] | undefined;
  districts: GeoUnit[] | undefined;
  thanas: GeoUnit[] | undefined;
  amOptions: OptionWithCount<HierarchyFilterOption>[] | undefined;
  zmOptions: OptionWithCount<HierarchyFilterOption>[] | undefined;
  leaveTypes: LeaveType[] | undefined;
}

function leaveTypeFields(
  isAttendanceClaim: boolean,
  leaveTypes: LeaveType[] | undefined,
): FilterField[] {
  if (isAttendanceClaim) return [];
  return [
    {
      key: 'leaveTypeId',
      label: 'Leave Type',
      type: 'select',
      options: toFilterOptions(leaveTypes?.filter((leaveType) => leaveType.isActive)),
    },
  ];
}

export function getLeaveApplicationFilterFields({
  isAttendanceClaim,
  hasDistrict,
  divisions,
  districts,
  thanas,
  amOptions,
  zmOptions,
  leaveTypes,
}: GetLeaveApplicationFilterFieldsArgs): FilterField[] {
  return [
    ...getDateFromToFilterFields({ fromKey: DATE_FROM_FILTER_KEY, toKey: DATE_TO_FILTER_KEY }),
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    { key: 'requestType', label: 'Request Type', type: 'select', options: REQUEST_TYPE_OPTIONS },
    ...leaveTypeFields(isAttendanceClaim, leaveTypes),
    {
      key: 'divisionId',
      label: 'Division',
      type: 'select',
      options: toFilterOptions(divisions),
      searchable: true,
    },
    {
      key: 'districtId',
      label: 'District',
      type: 'select',
      options: toFilterOptions(districts),
      searchable: true,
    },
    {
      key: 'thanaId',
      label: 'Thana',
      type: 'select',
      options: toFilterOptions(thanas),
      searchable: true,
      ...(hasDistrict ? {} : DISTRICT_FIRST_FIELD_STATE),
    },
    ...getSelectFieldsWhenOptionsExist('amUserId', 'AM Name', amOptions),
    ...getSelectFieldsWhenOptionsExist('zmUserId', 'ZM Name', zmOptions),
  ];
}
